import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "./Nav";
import { useLang } from "@/lib/i18n";

const line = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.13 },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Hero() {
  const { t } = useLang();

  return (
    <section className="relative bg-[#0F1E3D] overflow-hidden grid-pattern noise">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-[136px] lg:pt-[160px] pb-14 lg:pb-24">
        <motion.div {...fade(0.1)} className="flex items-center gap-3">
          <span className="w-2 h-2 rotate-45 bg-[#C5B358] pulse-dot" />
          <span className="eyebrow text-[#C5B358]">{t.hero.overline}</span>
        </motion.div>

        <h1 className="font-display mt-7 text-[42px] leading-[1.02] sm:text-6xl lg:text-[76px] font-bold text-white max-w-4xl">
          {t.hero.lines.map((txt, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === t.hero.serifLine ? (
                  <em className="font-serif-accent text-[#C5B358]">{txt}</em>
                ) : (
                  txt
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div {...fade(0.6)} className="mt-8 h-px w-24 bg-[#C5B358]/60" />

        <motion.p {...fade(0.7)} className="mt-8 text-base lg:text-lg text-[#A3B1C6] max-w-2xl leading-relaxed">
          {t.hero.tagline}
        </motion.p>

        <motion.div {...fade(0.82)} className="mt-10">
          <button
            data-testid="hero-cta-pricing"
            onClick={() => scrollToSection("plans")}
            className="bg-[#C5B358] hover:bg-[#B3A146] text-[#0F1E3D] font-bold text-xs uppercase tracking-[0.14em] rounded-full px-8 py-4 transition-all duration-300"
          >
            {t.hero.cta}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.95 }}
          className="mt-16 lg:mt-20 border-t border-[#C5B358]/20 pt-8 grid grid-cols-1 sm:grid-cols-3 gap-6"
        >
          {t.hero.stats.map(([value, label]) => (
            <div key={label} className="flex flex-col gap-1.5">
              <span className="font-display text-xl lg:text-2xl font-bold text-[#C5B358] tabular-nums">{value}</span>
              <span className="eyebrow text-[#A3B1C6]">{label}</span>
            </div>
          ))}
        </motion.div>

        <motion.button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollToSection("plans")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="hidden lg:flex mt-12 items-center gap-3 text-[#A3B1C6] hover:text-[#C5B358] transition-colors"
        >
          <ArrowDown size={15} className="animate-bounce" />
          <span className="eyebrow">{t.hero.scroll}</span>
        </motion.button>
      </div>
    </section>
  );
}